import {useIsRoktConsetGiven} from '@aboutyou/storage';
import {useScript} from '@aboutyou/ui-components';
import React, {
  Fragment,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import {RoktLancherProps} from './RoktLauncher';

interface Unsubscriber {
  unsubscribe: VoidFunction;
}

interface Placement {
  id: string;
}

interface SelectionEvent<T> {
  body: T;
  event?: string;
  placement?: Placement;
}

interface Subscriber<T> {
  subscribe: (handler: (value: T) => void) => Unsubscriber;
}

export interface Selection {
  close: () => Promise<void>;
  on: <T>(event: string) => Subscriber<SelectionEvent<T>>;
  ready: <T>() => Promise<T>;
}

interface SelectPlacementOptions {
  attributes: RoktLancherProps;
  identifier?: string;
}

interface RoktLauncherInstance {
  terminate: () => Promise<void>;
  selectPlacements: (options: SelectPlacementOptions) => Promise<Selection>;
}

const RoktLauncherContext = createContext<{
  isLoading: boolean;
  launcher: RoktLauncherInstance | null;
}>({launcher: null, isLoading: false});

export const useRoktLauncher = () => useContext(RoktLauncherContext);

const RoktLauncherContextProvider = ({children}: React.PropsWithChildren) => {
  const [launcher, setLauncher] = useState<RoktLauncherInstance | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {isLoading: isLoadingScript} = useRoktPlacement();

  // eslint-disable-next-line local-rules/too-complex-useeffects
  useEffect(() => {
    const createLauncher = async () => {
      const launcherInstance = await window.Rokt.createLauncher({
        accountId: '3150349908948827602',
        sandbox: process.env.NODE_ENV !== 'production',
      });

      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setLauncher(launcherInstance);
      setIsLoading(false);
    };

    if (!isLoadingScript) {
      createLauncher();
    }

    return () => {
      if (launcher) {
        launcher.terminate();
      }
    };
  }, [isLoadingScript]);

  return (
    <RoktLauncherContext.Provider value={{launcher, isLoading}}>
      {children}
    </RoktLauncherContext.Provider>
  );
};

export const RoktLauncherConditional = (props: React.PropsWithChildren) => {
  const isConsentBannerAccepted = useIsRoktConsetGiven();

  if (isConsentBannerAccepted) {
    return <RoktLauncherContextProvider {...props} />;
  }

  return <Fragment>{props.children}</Fragment>;
};

const useRoktPlacement = () => {
  const res = useScript(`https://apps.rokt.com/wsdk/integrations/launcher.js`, {
    async: true,
    id: 'rokt-launcher',
  });

  return {isLoading: res === 'pending' || res === 'idle'};
};
